import BrandIdentity, { SidebarBrand } from "@/components/theme/BrandIdentity";
import TransferNotifications from '@/components/TransferNotifications';
import { Outlet, NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { api, apiError } from "@/lib/pos-api";
import { getSelectedTenant, getActiveStore, selectMasterTenant, setActiveStore as persistStore } from "@/lib/scope";
import { useAuth } from "@/context/AuthContext";
import useRefreshCart from "@/hooks/useRefreshCart";
import { Bell, ChevronDown, ChevronUp, LogOut, PackagePlus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { navigationFor, ROLE_LABELS } from "@/lib/navigation";
import useSidebar from "@/hooks/useSidebar";
import "@/theme/layout.css";

function StockNotifications() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [seenAt, setSeenAt] = useState(() => localStorage.getItem("masivi_product_notifications_seen_at") || "");
  useEffect(() => {
    let active = true;
    const load = () => api.get("/product-notifications", { params: { limit: 20 } }).then(({ data }) => { if (active) setItems(data || []); }).catch(() => {});
    load();
    const timer = window.setInterval(load, 60000);
    return () => { active = false; window.clearInterval(timer); };
  }, []);
  const stockItems = items.filter(item => item.kind === "stock_empty" || item.kind === "stock_low");
  const unread = stockItems.length;
  const show = () => {
    setOpen(value => !value);
    if (!open) {
      const now = new Date().toISOString();
      localStorage.setItem("masivi_product_notifications_seen_at", now);
      setSeenAt(now);
    }
  };
  return <div className="relative z-20">
    <button type="button" className="theme-shortcut relative" aria-label="Peringatan stok" title="Peringatan stok" onClick={show}>
      <Bell size={19} />
      {unread > 0 && <span className="absolute -right-1 -top-1 min-w-4 h-4 px-1 rounded-full bg-danger text-white text-[10px] leading-4 text-center">{unread > 9 ? "9+" : unread}</span>}
    </button>
    {open && <div className="absolute right-0 top-11 w-[min(340px,calc(100vw-2rem))] rounded-xl border border-ui-line bg-ui-card shadow-xl p-3">
      <div className="flex items-center justify-between gap-3 mb-2"><strong className="text-sm">Peringatan stok</strong><button type="button" className="text-xs text-brand-readable" onClick={() => setOpen(false)}>Tutup</button></div>
      {stockItems.length === 0 ? <p className="text-xs text-ui-muted py-3">Stok aman.</p> : <div className="max-h-72 overflow-auto space-y-1">{stockItems.map(item => <div key={item.id} className={`rounded-lg px-3 py-2 ${item.kind === "stock_empty" ? "bg-danger-soft" : "bg-warning-soft"}`}><div className={`text-sm font-semibold truncate ${item.kind === "stock_empty" ? "text-danger-readable" : "text-warning-readable"}`}>{item.kind === "stock_empty" ? "Stok kosong · " : "Stok menipis · "}{item.name}</div><div className="text-[11px] text-ui-muted">Segera lakukan penerimaan atau pengisian stok</div></div>)}</div>}
    </div>}
  </div>;
}

function ReleaseNotifications() {
  const [items, setItems] = useState([]); const [open, setOpen] = useState(false);
  useEffect(() => { let active = true; const load = () => api.get("/product-notifications", { params: { limit: 20 } }).then(({ data }) => active && setItems((data || []).filter(item => item.kind === "product_created"))).catch(() => {}); load(); const t = setInterval(load, 60000); return () => { active = false; clearInterval(t); }; }, []);
  return <div className="relative z-20"><button type="button" className="theme-shortcut relative" aria-label="Rilis baru" title="Rilis baru" onClick={() => setOpen(v => !v)}><PackagePlus size={19} />{items.length > 0 && <span className="absolute -right-1 -top-1 min-w-4 h-4 px-1 rounded-full bg-brand-button text-white text-[10px] leading-4 text-center">{items.length > 9 ? "9+" : items.length}</span>}</button>{open && <div className="absolute right-0 top-11 w-[min(340px,calc(100vw-2rem))] rounded-xl border border-ui-line bg-ui-card shadow-xl p-3"><div className="flex items-center justify-between mb-2"><strong className="text-sm">Rilis baru</strong><button type="button" className="text-xs text-brand-readable" onClick={() => setOpen(false)}>Tutup</button></div>{items.length === 0 ? <p className="text-xs text-ui-muted py-3">Belum ada produk baru.</p> : <div className="max-h-72 overflow-auto space-y-1">{items.map(item => <div key={item.id} className="rounded-lg px-3 py-2 bg-ui-soft"><div className="text-sm font-semibold truncate">{item.name}</div><div className="text-[11px] text-ui-muted">{item.by} · {new Date(item.at).toLocaleString("id-ID")}</div></div>)}</div>}</div>}</div>;
}

function itemActive(item, location) {
  if (item.children) return item.children.some(child => itemActive(child, location));
  const [path, query = ""] = item.to.split("?");
  return location.pathname === path && (!query || location.search === `?${query}`);
}

function NavigationItem({ item, location, level = 0, onNavigate }) {
  const active = itemActive(item, location);
  const [open, setOpen] = useState(active);
  useEffect(() => { if (active) setOpen(true); }, [active]);
  if (item.children) {
    return <div className="space-y-1">
      <button
        type="button"
        aria-expanded={open}
        data-testid={`nav-group-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        onClick={() => setOpen(value => !value)}
        className={`w-full flex items-center gap-3 rounded-xl px-3 h-12 transition-colors no-select ${active ? "bg-brand-soft text-brand-readable font-bold" : "text-ui-muted hover:bg-ui-soft hover:text-ui-text"}`}
        style={{ paddingLeft: `${12 + level * 12}px` }}
      >
        <item.icon className="h-5 w-5 shrink-0" />
        <span className="text-sm flex-1 text-left">{item.label}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="space-y-1" data-testid={`nav-group-content-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
        {item.children.map(child => <NavigationItem key={child.to || child.label} item={child} location={location} level={level + 1} onNavigate={onNavigate} />)}
      </div>}
    </div>;
  }
  return <Link
    to={item.to}
    onClick={onNavigate}
    aria-label={item.label}
    title={item.label}
    data-testid={`nav-${item.to.slice(1).replace(/[^a-z0-9]+/gi, "-")}`}
    className={`flex items-center gap-3 rounded-xl pr-3 h-11 transition-colors no-select ${active ? "bg-brand-button text-ui-text font-bold" : "text-ui-muted hover:bg-ui-soft hover:text-ui-text"}`}
    style={{ paddingLeft: `${12 + level * 12}px` }}
  >
    <item.icon className="h-4 w-4 shrink-0" />
    <span className="text-sm">{item.label}</span>
  </Link>;
}



export default function AppLayout() {
  const { user, logout, tenant, readOnly } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile, navHidden, setNavHidden } = useSidebar(location.pathname);
  const [topBarHidden, setTopBarHidden] = useState(false);
  useEffect(() => {
    if (location.pathname !== "/pos") setTopBarHidden(false);
  }, [location.pathname]);
  const menuButton = useRef(null);
  const { posDraft, setPosDraft, draftRequestId, recovering, busy: recoveringBusy, error: recoveryError, storageError, recover } = useRefreshCart(tenant?.id, user.id, !readOnly && ["admin", "cashier"].includes(user.role));
  useEffect(() => {
    const navigateShortcut = (event) => {
      if (!["F1", "F2"].includes(event.key) || event.ctrlKey || event.altKey || event.metaKey) return;
      event.preventDefault();
      if (event.repeat) return;
      if (event.key === "F1" && user.role === "admin") navigate("/products");
      if (event.key === "F2" && ["admin", "cashier"].includes(user.role) && location.pathname !== "/pos") navigate("/pos");
    };
    window.addEventListener("keydown", navigateShortcut);
    return () => window.removeEventListener("keydown", navigateShortcut);
  }, [navigate, user.role, location.pathname]);
  const groups = navigationFor(user.role, tenant?.kind);

  const [masterTenants, setMasterTenants] = useState([]);
  const selectedTenant = getSelectedTenant();
  const [tenantListError, setTenantListError] = useState("");
  useEffect(() => {
    const load = () => { if (readOnly) api.get("/master/tenants").then((r) => { setMasterTenants(r.data); setTenantListError(""); }).catch(e => setTenantListError(apiError(e))); };
    load(); window.addEventListener("pos-tenants-updated", load);
    return () => window.removeEventListener("pos-tenants-updated", load);
  }, [readOnly]);
  const [stores, setStores] = useState([]);
  const [activeStore, setActiveStore] = useState(getActiveStore());
  const [storeMenuOpen, setStoreMenuOpen] = useState(false);
  useEffect(() => {
    api.get("/stores").then((r) => {
      setStores(r.data);
      const preferred = user.store_id || getActiveStore() || r.data.find((store) => store.is_default)?.id || r.data[0]?.id || '';
      setActiveStore(preferred);
      persistStore(preferred);
    }).catch(() => {});
  }, [user.role, user.store_id, selectedTenant]);

  const changeStore = (next) => {
    setActiveStore(next);
    persistStore(next);
    window.location.reload();
  };

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const [showLogout, setShowLogout] = useState(false);
  const lpTimer = useRef(null);
  const startLongPress = () => {
    clearTimeout(lpTimer.current);
    lpTimer.current = setTimeout(() => setShowLogout(true), 600);
  };
  const cancelLongPress = () => clearTimeout(lpTimer.current);

  useEffect(() => () => clearTimeout(lpTimer.current), []);
  const sidebarContent = <>
        <SidebarBrand />

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {groups.map((g, gi) => (
            <div key={gi} className="space-y-1">
              {g.section && (
                <div className="block px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-widest text-ui-muted">
                  {g.section}
                </div>
              )}
              {g.items.map((item) => <NavigationItem key={item.to || item.label} item={item} location={location} onNavigate={() => { if (isMobile) setNavHidden(true); }} />)}
            </div>
          ))}
        </nav>

        <div className="p-3 border-t border-ui-line">

          <div
            className="flex items-center gap-3 px-3 py-2 mb-2 rounded-lg cursor-pointer select-none hover:bg-ui-soft"
            title="Tekan &amp; tahan untuk menampilkan tombol Keluar"
            onPointerDown={startLongPress}
            onPointerUp={cancelLongPress}
            onPointerLeave={cancelLongPress}
          >
            <div className="h-9 w-9 rounded-full bg-ui-muted-surface flex items-center justify-center text-sm font-bold">
              {user.name?.[0]?.toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{user.name}</div>
              <div className="text-[11px] text-brand-readable uppercase tracking-wide">
                {ROLE_LABELS[user.role] || user.role}
              </div>
            </div>
          </div>
          {showLogout ? (
            <Button
              data-testid="logout-button"
              variant="ghost"
              onClick={doLogout}
              className="w-full justify-start gap-2 text-ui-muted hover:text-danger-readable hover:bg-danger-soft h-11"
            >
              <LogOut className="h-5 w-5" />
              <span className="inline">Keluar</span>
            </Button>
          ) : (
            <button
              data-testid="logout-reveal"
              onPointerDown={startLongPress}
              onPointerUp={cancelLongPress}
              onPointerLeave={cancelLongPress}
              className="w-full flex items-center justify-center gap-2 text-ui-muted hover:text-ui-muted h-8 text-[11px]"
              title="Tekan &amp; tahan untuk keluar"
            >
              <LogOut className="h-4 w-4" />
              <span className="inline">Tekan &amp; tahan untuk keluar</span>
            </button>
          )}
        </div>
  </>;
  return (
    <div className="h-screen flex bg-ui-canvas text-ui-text overflow-hidden app-shell" data-testid="app-shell">
      {isMobile ? <Sheet open={!navHidden} onOpenChange={open => setNavHidden(!open)}><SheetContent side="left" id="app-sidebar" className="masivi-sidebar app-mobile-sidebar" data-testid="app-sidebar" onCloseAutoFocus={e => { e.preventDefault(); menuButton.current?.focus(); }}>
        <SheetTitle className="sr-only">Menu aplikasi</SheetTitle><SheetDescription className="sr-only">Navigasi sesuai hak akses akun. Pilih menu untuk membuka halaman.</SheetDescription>
        {sidebarContent}
      </SheetContent></Sheet> : !navHidden && <aside id="app-sidebar" data-testid="app-sidebar" aria-label="Menu aplikasi" className="masivi-sidebar app-desktop-sidebar border-r border-ui-line flex flex-col shrink-0">{sidebarContent}</aside>}


      {/* Content */}
      <main className="flex-1 min-w-0 overflow-hidden flex flex-col">
        {!topBarHidden ? <div className="relative app-topbar"><BrandIdentity menuButtonRef={menuButton} menuOpen={!navHidden} onToggleMenu={() => setNavHidden(value => !value)} storeControl={<div className="relative app-store-control"><button type="button" data-testid="header-store-name" title="Cabang aktif" aria-label="Pilih cabang" aria-expanded={storeMenuOpen} onClick={() => !user.store_id && stores.length > 1 && setStoreMenuOpen(v => !v)} className="max-w-[170px] sm:max-w-[230px] truncate rounded-lg border border-ui-line bg-ui-card/90 px-2.5 py-1.5 text-[11px] sm:text-xs font-semibold text-ui-text">{stores.find(store => store.id === activeStore)?.name || "Cabang"}<span className="ml-1 text-ui-muted">⌄</span></button>{storeMenuOpen && <div className="absolute left-0 top-11 z-40 w-56 rounded-xl border border-ui-line bg-ui-card p-1.5 shadow-xl">{stores.map(store => <button type="button" key={store.id} onClick={() => { setStoreMenuOpen(false); changeStore(store.id); }} className={`w-full rounded-lg px-3 py-2.5 text-left text-xs hover:bg-ui-soft ${store.id === activeStore ? "bg-brand-soft text-brand-readable font-semibold" : "text-ui-text"}`}>{store.name}</button>)}</div>}</div>} /><div className="app-header-tools absolute right-3 top-2 flex items-center gap-2"><div id="desktop-online-target" className="hidden min-[641px]:flex items-center" /><div id="desktop-cashier-tools" className="hidden min-[641px]:flex items-center gap-2 order-first" /><ReleaseNotifications /><TransferNotifications /><StockNotifications /><button type="button" data-testid="hide-topbar-btn" aria-label="Sembunyikan bar atas" title="Sembunyikan bar atas" onClick={() => setTopBarHidden(true)} className="h-8 w-8 rounded-lg border border-ui-line bg-ui-card text-ui-text shadow-sm order-5 xl:hidden"><ChevronUp className="mx-auto h-4 w-4" /></button></div></div> : <button type="button" data-testid="show-topbar-btn" aria-label="Tampilkan bar atas" title="Tampilkan bar atas" onClick={() => setTopBarHidden(false)} className="fixed left-2 top-2 z-50 h-8 w-8 rounded-lg border border-ui-line bg-ui-card text-ui-text shadow-lg xl:hidden"><ChevronDown className="mx-auto h-4 w-4" /></button>}
        {readOnly && <header className="shrink-0 border-b border-warning bg-warning-soft px-3 py-2 flex flex-wrap items-center gap-2" data-testid="master-view-bar">
          <span className="text-sm font-semibold">{tenant.name}</span>
          <NavLink className="text-sm underline text-brand-readable" to="/master/tenants">Kelola tenant</NavLink>
          <NavLink className="text-sm underline text-brand-readable" to="/master/system">Status VPS</NavLink>
          <label htmlFor="master-tenant" className="sr-only">Tenant yang ditampilkan</label>
          <select id="master-tenant" data-testid="tenant-selector" value={selectedTenant} onChange={e => selectMasterTenant(e.target.value)}
            className="w-full sm:w-64 h-10 rounded-lg border border-warning bg-ui-card px-2 text-sm">
            <option value="">Pilih tenant… (data kosong)</option>
            {masterTenants.map(t => <option key={t.id} value={t.id}>{t.name} · {t.host}</option>)}
          </select>
          {stores.length > 0 && <span className="text-sm text-ui-muted">{stores.find((store) => store.id === activeStore)?.name || stores[0].name}</span>}
          <span role="status" className="text-xs text-warning-readable">{location.pathname.startsWith("/master/") ? "Administrasi master" : selectedTenant ? "Hanya baca" : "Pilih tenant untuk menampilkan data"}</span>
          {tenantListError && <span role="alert" className="text-sm text-danger-readable">{tenantListError}</span>}
        </header>}
        {storageError && <p role="alert" className="p-3 bg-warning-soft text-warning-readable">{storageError}</p>}
        {recovering ? <div data-testid="cart-refresh-recovery" className="m-auto max-w-lg p-6 space-y-3 text-center">
          <h1 className="text-lg font-semibold">Menyimpan keranjang ke Antrian</h1>
          <p>Keranjang sebelum refresh tetap tersimpan di browser sampai berhasil masuk ke Antrian.</p>
          {recoveryError && <p role="alert" className="text-danger-readable">{recoveryError}</p>}
          <Button data-testid="cart-refresh-retry" onClick={recover} disabled={recoveringBusy}>{recoveringBusy ? "Menyimpan…" : "Coba Lagi"}</Button>
        </div> : <Outlet context={{ posDraft, setPosDraft, draftRequestId, topBarHidden, setTopBarHidden }} />}
      </main>
    </div>
  );
}
